import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPortalPage } from '../actions/blanket';
import { Card, Button, Table, Row, Col, Form, InputGroup, ButtonGroup } from '@themesberg/react-bootstrap';
import { useForm } from 'react-hook-form';
import Datetime from 'react-datetime';
import moment from 'moment-timezone';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import reportApi from '../services/report-service';
import Skeleton from 'react-loading-skeleton';
import { SkeletonWrapper } from '../components/TableRowWrapper';
import { generatePdfReportAndDownload } from '../pdfGenerator';
import './styles.scss';
import ContainerCategoryPreview from "./containercategorypreview";
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { addCompanyHeader } from "../excelreportgenerator";


const ContainerCategory = () => {
    const defaultStartDate = moment().startOf('month').toDate();
    const defaultEndDate = moment().endOf('month').toDate();
    
    const dispatch = useDispatch();
    const { getContainerReportByLoadingDate } = reportApi;
    const loading = useSelector((state) => state.blanket.showAdminLoader);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [reportList, setReportList] = useState([]);
    const [downloading, setDownloading] = useState(false);

    const formSchema = Yup.object().shape({
        startDate: Yup.string().nullable().required('Start date is required')
            .test('start-date-test', 'Start date cannot be greater than end date', function(value) {
                const { endDate } = this.parent;
                return !value || !endDate || moment(value).isSameOrBefore(moment(endDate));
            }),
        endDate: Yup.string().nullable().required('End date is required')
            .test('end-date-test', 'End date cannot be earlier than start date', function(value) {
                const { startDate } = this.parent;
                return !value || !startDate || moment(value).isSameOrAfter(moment(startDate));
            }),
    });

    const formOptions = {
        resolver: yupResolver(formSchema),
        mode: 'all',
        defaultValues: {
            startDate: defaultStartDate,
            endDate: defaultEndDate,
        }
    };

    const { getValues, trigger, formState, setValue, reset } = useForm(formOptions);
    const { errors } = formState;

    const handleStartDateChange = (date) => {
        setStartDate(date);
        setValue('startDate', date, { shouldValidate: true });
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setValue('endDate', date, { shouldValidate: true });
    };

    const resetFilters = () => {
        reset({
            startDate: defaultStartDate,
            endDate: defaultEndDate,
        });
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        setReportList([]);
    };

    const handleGenerateReport = async () => {
        try {
            const isValid = await trigger();
    
            if (!isValid) {
                return;
            }
    
            const { startDate, endDate } = getValues();
            const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : undefined;
            const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : undefined;
    
            const response = await getContainerReportByLoadingDate(formattedStartDate, formattedEndDate);
            setReportList(response?.data || []);
        } catch (error) {
            console.log("Error while generating container category report: ", error);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : 'start';
            const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : 'end';
    
            const fileName = `container_category_report_${formattedStartDate}_${formattedEndDate}.pdf`;
            const pdfContent = document.getElementById('element-to-print-container-category-report').innerHTML;
    
            await generatePdfReportAndDownload(pdfContent, fileName);

            setDownloading(false);
    
        } catch (error) {
            console.error('Error in downloading PDF:', error);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            setDownloading(true);
    
            const { startDate, endDate } = getValues();
            const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : undefined;
            const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : undefined;
    
            // Fetch the report data
            const response = await getContainerReportByLoadingDate(formattedStartDate, formattedEndDate);
            const reportData = response?.data || [];
    
            // Calculate overall totals
            const totalContainers = reportData.reduce((acc, item) => acc + item.numberOfContainers, 0);
    
            // Create a new workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Container Category Report');
    
            // Add company header, title, and date range to the worksheet
            await addCompanyHeader(worksheet, 'Container Category Report', formattedStartDate, formattedEndDate);

            worksheet.getRow(12).height = 20;
            worksheet.getRow(13).height = 20;
    
            // Add column headers
            const headerRow = worksheet.addRow([
                'Loading Date',
                'Number of Containers',
                'Container List'
            ]);
    
            // Style the column headers
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFEAEAEA' }
                };
                cell.alignment = { horizontal: 'center' };
            });
    
            // Set column widths for better visibility
            worksheet.columns = [
                { key: 'loadingDate', width: 20 },
                { key: 'numberOfContainers', width: 20 },
                { key: 'containers', width: 40 }
            ];
    
            // Add data rows
            reportData.forEach((item) => {
                worksheet.addRow([
                    moment(item.loadingDate).format('YYYY-MM-DD'),
                    item.numberOfContainers,
                    item.containers.join(', ')
                ]);
            });
    
            // Add totals at the bottom
            const lastRow = worksheet.lastRow.number + 1;
    
            // Add total number of containers
            worksheet.getCell(`A${lastRow}`).value = 'Total Containers:';
            worksheet.getCell(`A${lastRow}`).font = { bold: true, size: 16 };
            worksheet.getCell(`B${lastRow}`).value = totalContainers;
    
            // Save the Excel file
            const excelFileName = `container_category_report_${formattedStartDate}_${formattedEndDate}.xlsx`;
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer]), excelFileName);
            });
    
            setDownloading(false);
        } catch (error) {
            console.log("Error while generating Excel report: ", error);
            setDownloading(false);
        }
    };
    

    const handleDownloadReport = async () => {
        try {
            setDownloading(true);

            setTimeout(() => {
                handleDownloadPdf();
            }, 100);
        } catch (error) {
            console.log("Error while generating container category report: ", error);
        }
    };

    useEffect(() => {
        dispatch(setPortalPage('List of Container Category Count'));
    }, [dispatch]);

    return (
        <>
            <Card border="light" className="table-wrapper table-responsive shadow-sm card-wrapper-full-height">
                <Card.Body className="pt-0">
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                        <div className="d-block mb-4 mb-md-0">
                            <p className="mb-0">List of Container Category (LCL & FCL) Count</p>
                        </div>
                    </div>
                    <div className="table-settings mb-2">
                        <Row className="justify-content-between align-items-center">
                            <Col md={6}>
                                <Form.Group id="start-date">
                                    <Form.Label>Start Date</Form.Label>
                                    <Datetime
                                        timeFormat={false}
                                        onChange={handleStartDateChange}
                                        closeOnSelect={true}
                                        renderInput={(props, openCalendar) => (
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <FontAwesomeIcon icon={faCalendar} />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="startDate"
                                                    value={startDate ? moment(startDate).format('MM/DD/YYYY') : ''}
                                                    placeholder="Select date"
                                                    onFocus={openCalendar}
                                                    autoComplete="off"
                                                />
                                            </InputGroup>
                                        )}
                                    />
                                    {errors.startDate?.message && (
                                        <small className="invalid-fields">{errors.startDate.message}</small>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group id="end-date">
                                    <Form.Label>End Date</Form.Label>
                                    <Datetime
                                        timeFormat={false}
                                        onChange={handleEndDateChange}
                                        closeOnSelect={true}
                                        renderInput={(props, openCalendar) => (
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <FontAwesomeIcon icon={faCalendar} />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="endDate"
                                                    value={endDate ? moment(endDate).format('MM/DD/YYYY') : ''}
                                                    placeholder="Select date"
                                                    onFocus={openCalendar}
                                                    autoComplete="off"
                                                />
                                            </InputGroup>
                                        )}
                                    />
                                    {errors.endDate?.message && (
                                        <small className="invalid-fields">{errors.endDate.message}</small>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                    <div className="table-settings mb-5">
                        <Row>
                            <Col md={12}>
                                <Button variant="primary" onClick={() => handleGenerateReport()}>
                                    Search
                                </Button>
                                <Button className="ms-2" variant="secondary" onClick={() => resetFilters()}>
                                    Reset
                                </Button>
                                {reportList.length > 0 && (
                                    <ButtonGroup className="pull-right ms-2">
                                        <Button variant="primary" size="sm" type="button" onClick={() => handleDownloadReport()}>
                                            <FontAwesomeIcon icon={faFilePdf} /> {`${downloading ? 'Downloading...' : 'Download PDF'}`}
                                        </Button>
                                        <Button variant="secondary" size="sm" type="button" onClick={() => handleDownloadExcel()}>
                                            <FontAwesomeIcon icon={faFileExcel} /> {`${downloading ? 'Downloading...' : 'Download Excel'}`}
                                        </Button>
                                    </ButtonGroup>
                                )}
                            </Col>
                        </Row>
                    </div>
                    <Table hover className="billing-table-list align-items-center">
                        <thead>
                            <tr>
                                <th className="border-bottom">Loading Date</th>
                                <th className="border-bottom">Number of Containers</th>
                                <th className="border-bottom">Container List</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportList.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <span className="fw-normal">
                                            {loading ? <Skeleton wrapper={SkeletonWrapper} /> : moment(item.loadingDate).format('YYYY-MM-DD')}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="fw-normal">
                                            {loading ? <Skeleton wrapper={SkeletonWrapper} /> : item.numberOfContainers}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="fw-normal">
                                            {loading ? <Skeleton wrapper={SkeletonWrapper} /> : item.containers.join(', ')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            <div className='report-element-to-print' id='element-to-print-container-category-report'>
                <ContainerCategoryPreview 
                    startDate={startDate} 
                    endDate={endDate} 
                    reportList={reportList}
                />
            </div>
        </>
    );
};

export default ContainerCategory;
